<template>
  <div>
    <header class="wsp-toolbar">
      <div class="wsp-toolbar__title">
        <span class="wsp-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Products</h1>
          <p>Catalog items shown on the public storefront</p>
        </div>
      </div>
      <div class="wsp-toolbar__actions">
        <div class="wsp-search">
          <span class="wsp-search__icon">🔍</span>
          <input v-model.trim="search" type="text" placeholder="Search products…" @keyup.enter="fetch" />
        </div>
        <button class="wsp-btn-refresh" :class="{ 'is-loading': loading }" :disabled="loading" @click="fetch">
          <svg class="wsp-btn-refresh__icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ loading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
        <button class="wsp-btn-primary" @click="openCreate">+ Add Product</button>
      </div>
    </header>

    <CAlert v-if="error" color="danger" show>{{ error }}</CAlert>

    <section class="filter-bar">
      <div class="filter-row">
        <span class="filter-label">Source</span>
        <div class="seg" role="tablist">
          <button
            v-for="opt in sourceOptions"
            :key="opt.value"
            class="seg__btn"
            :class="{ 'seg__btn--active': source === opt.value }"
            role="tab"
            :aria-selected="source === opt.value"
            @click="setSource(opt.value)"
          >{{ opt.label }}</button>
        </div>
      </div>

      <div v-if="categories.length" class="filter-row filter-row--cats">
        <span class="filter-label">Category</span>
        <div class="cat-scroll">
          <button
            class="cat-chip"
            :class="{ 'cat-chip--active': !category }"
            @click="setCategory('')"
          >All</button>
          <button
            v-for="c in categories"
            :key="c"
            class="cat-chip"
            :class="{ 'cat-chip--active': category === c }"
            @click="setCategory(c)"
          >{{ c }}</button>
        </div>
      </div>
    </section>

    <section class="wsp-card">
      <header class="wsp-card__head">
        <h2>All Products</h2>
        <span class="wsp-card__count">{{ total }} product(s)</span>
      </header>

      <div v-if="loading" class="wsp-loading"><CSpinner size="sm" /> Loading…</div>
      <div v-else-if="!rows.length" class="wsp-empty">
        <div class="wsp-empty__title">No products found</div>
        <div>Try a different search or add your first product.</div>
      </div>
      <div v-else>
        <table class="wsp-table">
          <thead>
            <tr>
              <th style="width:60px;">Image</th>
              <th>Name</th>
              <th>Source</th>
              <th>Categories</th>
              <th>Price</th>
              <th>Position</th>
              <th class="wsp-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="`${row.source}-${row.id}`">
              <td>
                <img v-if="firstImage(row.images)" :src="firstImage(row.images)" class="wsp-thumb" alt="" />
                <span v-else style="color:#9ca3af;">—</span>
              </td>
              <td>
                <strong>{{ row.name }}</strong>
                <div v-if="row.sku" style="font-size:0.74rem;color:#6b7280;">SKU: {{ row.sku }}</div>
              </td>
              <td>
                <span class="src-pill" :class="`src-pill--${row.source}`">{{ sourceLabel(row.source) }}</span>
              </td>
              <td style="font-size:0.78rem;color:#4b5563;">{{ row.categories || '—' }}</td>
              <td>
                <div v-if="row.sale_price && row.regular_price && Number(row.sale_price) < Number(row.regular_price)">
                  <strong>₹{{ row.sale_price }}</strong>
                  <span style="text-decoration:line-through;color:#9ca3af;font-size:0.75rem;margin-left:6px;">₹{{ row.regular_price }}</span>
                </div>
                <div v-else>
                  <strong>₹{{ row.sale_price || row.regular_price || '—' }}</strong>
                </div>
              </td>
              <td>{{ row.position == null ? '—' : row.position }}</td>
              <td class="wsp-actions">
                <button class="wsp-icon-btn" @click="openEdit(row)" title="Edit">✎</button>
                <button class="wsp-icon-btn wsp-icon-btn--danger" @click="confirmDelete(row)" title="Delete">🗑</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="modalOpen" class="wsp-modal-backdrop" @click.self="closeModal">
      <div class="wsp-modal wsp-modal--wide">
        <header class="wsp-modal__head">
          <h3>{{ form.id ? 'Edit Product' : 'Add Product' }}</h3>
          <button class="wsp-modal__close" @click="closeModal">×</button>
        </header>
        <div class="wsp-modal__body">
          <div class="wsp-form-grid">
            <div class="wsp-field wsp-field--full">
              <label>Source <span style="color:#b91c1c">*</span></label>
              <select v-model="form.source" :disabled="!!form.id">
                <option value="drugs">Drugs</option>
                <option value="agro">Agro</option>
                <option value="vet">Veterinary</option>
              </select>
              <small v-if="form.id" style="color:#6b7280;">Source can't be changed after creation.</small>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Name <span style="color:#b91c1c">*</span></label>
              <input v-model="form.name" type="text" />
              <span v-if="errors.name" class="wsp-field__error">{{ errors.name }}</span>
            </div>
            <div class="wsp-field">
              <label>SKU</label>
              <input v-model="form.sku" type="text" />
            </div>
            <div class="wsp-field">
              <label>Type</label>
              <input v-model="form.type" type="text" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Short Description</label>
              <textarea v-model="form.short_description" rows="2"></textarea>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Description (HTML allowed)</label>
              <textarea v-model="form.description" rows="6"></textarea>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Categories (comma-separated)</label>
              <input v-model="form.categories" type="text" placeholder="painkillers, ayurveda, otc" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Tags (comma-separated)</label>
              <input v-model="form.tags" type="text" placeholder="featured, new" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Images</label>

              <div
                class="img-drop"
                :class="{ 'img-drop--over': dragOver, 'img-drop--busy': uploading }"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="onDrop($event)"
                @click="$refs.imgInput.click()"
              >
                <input
                  ref="imgInput"
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  @change="onPickFiles"
                />
                <div v-if="uploading" class="img-drop__hint">
                  <CSpinner size="sm" /> Uploading {{ uploadDone }}/{{ uploadTotal }}…
                </div>
                <div v-else class="img-drop__hint">
                  <strong>Click or drop images here</strong>
                  <span>JPG, PNG, WEBP, GIF, SVG · up to 10 MB each</span>
                </div>
              </div>
              <span v-if="errors.upload" class="wsp-field__error">{{ errors.upload }}</span>

              <div v-if="imageList.length" class="img-grid">
                <div
                  v-for="(u, i) in imageList"
                  :key="i"
                  class="img-thumb"
                >
                  <img :src="u" alt="" />
                  <button type="button" class="img-thumb__del" :title="'Remove'" @click.stop="removeImage(i)">×</button>
                </div>
              </div>

              <details class="img-url-fallback">
                <summary>Or paste URLs (comma-separated)</summary>
                <textarea v-model="form.images" rows="2" placeholder="https://…/img1.jpg, https://…/img2.jpg"></textarea>
              </details>
            </div>
            <div class="wsp-field">
              <label>Regular Price (₹)</label>
              <input v-model="form.regular_price" type="number" step="0.01" />
            </div>
            <div class="wsp-field">
              <label>Sale Price (₹)</label>
              <input v-model="form.sale_price" type="number" step="0.01" />
            </div>
            <div class="wsp-field">
              <label>Stock Quantity</label>
              <input v-model.number="form.stock" type="number" />
            </div>
            <div class="wsp-field">
              <label>Position</label>
              <input v-model.number="form.position" type="number" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label class="wsp-toggle">
                <input v-model="form.in_stock" type="checkbox" /> In stock
              </label>
            </div>

            <template v-if="form.source === 'drugs'">
              <div class="wsp-field wsp-field--full">
                <label>Disease Details</label>
                <textarea v-model="form.disease_details" rows="3"></textarea>
              </div>
              <div class="wsp-field wsp-field--full">
                <label>Contents</label>
                <textarea v-model="form.contents" rows="3"></textarea>
              </div>
              <div class="wsp-field wsp-field--full">
                <label>How to use</label>
                <textarea v-model="form.how_to_use" rows="3"></textarea>
              </div>
            </template>

            <template v-else>
              <div class="wsp-field wsp-field--full">
                <label>Accordion Sections (JSON)</label>
                <textarea v-model="form.accordion_sections" rows="6" placeholder='[{"title":"Usage","body":"…"}]'></textarea>
                <span v-if="errors.accordion_sections" class="wsp-field__error">{{ errors.accordion_sections }}</span>
              </div>
            </template>
          </div>
        </div>
        <footer class="wsp-modal__foot">
          <button class="wsp-btn-ghost" :disabled="saving" @click="closeModal">Cancel</button>
          <button class="wsp-btn-primary" :disabled="saving" @click="save">
            {{ saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create product') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import websitesApi from '@/api/websites'

const empty = () => ({
  id: null, source: 'drugs',
  name: '', sku: '', type: '',
  short_description: '', description: '',
  categories: '', tags: '', images: '',
  regular_price: '', sale_price: '',
  in_stock: true, stock: 0, position: null,
  disease_details: '', contents: '', how_to_use: '',
  accordion_sections: '',
})

const SOURCE_LABELS = { drugs: 'Drugs', agro: 'Agro', vet: 'Veterinary' }

export default {
  name: 'WebsitesProducts',
  data () {
    return {
      rows: [], total: 0, loading: false, error: '',
      search: '', category: '', source: 'all',
      modalOpen: false, saving: false,
      form: empty(), errors: {},
      dragOver: false, uploading: false, uploadDone: 0, uploadTotal: 0,
      sourceOptions: [
        { value: 'all',   label: 'All' },
        { value: 'drugs', label: 'Drugs' },
        { value: 'agro',  label: 'Agro' },
        { value: 'vet',   label: 'Veterinary' },
      ],
    }
  },
  mounted () { this.fetchAll() },
  watch: {
    search () { this.debouncedFetch() },
  },
  computed: {
    imageList () {
      if (!this.form.images) return []
      return String(this.form.images).split(',').map(s => s.trim()).filter(Boolean)
    },
    categories () {
      const set = new Set()
      for (const row of this.rows) {
        if (!row.categories) continue
        for (const c of String(row.categories).split(',')) {
          const t = c.trim()
          if (t) set.add(t)
        }
      }
      return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    },
  },
  methods: {
    async fetchAll () {
      this.loading = true; this.error = ''
      try {
        const params = { source: this.source }
        if (this.search)   params.search   = this.search
        if (this.category) params.category = this.category
        const data = await websitesApi.productsList(params)
        this.rows  = data.products || []
        this.total = Number(data.total || this.rows.length)
      } catch (e) { this.error = this.extractError(e) }
      finally { this.loading = false }
    },
    fetch () { return this.fetchAll() },
    debouncedFetch () {
      clearTimeout(this._t)
      this._t = setTimeout(() => this.fetchAll(), 280)
    },
    setSource (s) {
      this.source = s
      this.category = ''
      this.fetchAll()
    },
    setCategory (c) { this.category = c; this.fetch() },
    sourceLabel (s) { return SOURCE_LABELS[s] || s },
    onPickFiles (e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      this.uploadFiles(files)
    },
    onDrop (e) {
      this.dragOver = false
      const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith('image/'))
      this.uploadFiles(files)
    },
    async uploadFiles (files) {
      if (!files.length || this.uploading) return
      this.errors = { ...this.errors, upload: '' }
      this.uploading = true
      this.uploadTotal = files.length
      this.uploadDone = 0
      const newUrls = []
      try {
        for (const f of files) {
          try {
            const data = await websitesApi.uploadImage(f, 'websites/products')
            if (data && data.url) newUrls.push(data.url)
          } catch (err) {
            this.errors = { ...this.errors, upload: this.extractError(err) }
          }
          this.uploadDone++
        }
        if (newUrls.length) {
          const existing = this.imageList
          this.form.images = [...existing, ...newUrls].join(', ')
        }
      } finally {
        this.uploading = false
      }
    },
    removeImage (index) {
      const list = this.imageList.slice()
      list.splice(index, 1)
      this.form.images = list.join(', ')
    },
    firstImage (raw) {
      if (!raw) return null
      const first = String(raw).split(',')[0].trim()
      return first || null
    },
    imagePreview (raw) {
      if (!raw) return []
      return String(raw).split(',').map(s => s.trim()).filter(Boolean).slice(0, 6)
    },
    openCreate () {
      this.form = empty()
      if (['drugs', 'agro', 'vet'].includes(this.source)) this.form.source = this.source
      this.errors = {}
      this.resetUploader()
      this.modalOpen = true
    },
    resetUploader () {
      this.dragOver = false
      this.uploading = false
      this.uploadDone = 0
      this.uploadTotal = 0
    },
    openEdit (row) {
      const src = row.source || 'drugs'
      this.form = {
        id: row.id,
        source: src,
        name: row.name || '',
        sku: row.sku || '',
        type: row.type || '',
        short_description: row.short_description || '',
        description: row.description || '',
        categories: row.categories || '',
        tags: row.tags || '',
        images: row.images || '',
        regular_price: row.regular_price == null ? '' : row.regular_price,
        sale_price:    row.sale_price    == null ? '' : row.sale_price,
        in_stock: !!row.in_stock,
        stock: row.stock == null ? 0 : Number(row.stock),
        position: row.position == null ? null : Number(row.position),
        disease_details: row.disease_details || '',
        contents: row.contents || '',
        how_to_use: row.how_to_use || '',
        accordion_sections: this.serializeAccordion(row.accordion_sections),
      }
      this.errors = {}
      this.resetUploader()
      this.modalOpen = true
    },
    serializeAccordion (val) {
      if (val == null || val === '') return ''
      if (typeof val === 'string') return val
      try { return JSON.stringify(val, null, 2) } catch (_) { return '' }
    },
    closeModal () { if (!this.saving) this.modalOpen = false },
    async save () {
      this.errors = {}
      if (!this.form.name.trim()) { this.errors.name = 'Name is required'; return }

      const payload = {
        source: this.form.source,
        name: this.form.name,
        sku: this.form.sku,
        type: this.form.type,
        short_description: this.form.short_description,
        description: this.form.description,
        categories: this.form.categories,
        tags: this.form.tags,
        images: this.form.images,
        in_stock: this.form.in_stock,
        regular_price: this.form.regular_price === '' ? null : this.form.regular_price,
        sale_price:    this.form.sale_price    === '' ? null : this.form.sale_price,
        position: this.form.position == null || this.form.position === '' ? null : Number(this.form.position),
        stock: this.form.stock == null || this.form.stock === '' ? 0 : Number(this.form.stock),
      }

      if (this.form.source === 'drugs') {
        payload.disease_details = this.form.disease_details
        payload.contents        = this.form.contents
        payload.how_to_use      = this.form.how_to_use
      } else {
        const raw = (this.form.accordion_sections || '').trim()
        if (raw === '') {
          payload.accordion_sections = null
        } else {
          try {
            payload.accordion_sections = JSON.parse(raw)
          } catch (_) {
            this.errors.accordion_sections = 'Invalid JSON'
            return
          }
        }
      }

      this.saving = true
      try {
        if (this.form.id) await websitesApi.productUpdate(this.form.id, payload)
        else              await websitesApi.productCreate(payload)
        this.modalOpen = false
        await this.fetchAll()
      } catch (e) { this.error = this.extractError(e) }
      finally { this.saving = false }
    },
    async confirmDelete (row) {
      if (!confirm(`Delete product "${row.name}"?`)) return
      try { await websitesApi.productDelete(row.id, row.source); await this.fetch() }
      catch (e) { this.error = this.extractError(e) }
    },
    extractError (e) {
      return (e && e.response && e.response.data && (e.response.data.error || e.response.data.message))
        || (e && e.message) || 'Request failed'
    },
  },
}
</script>

<style scoped>
.filter-bar {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 12px 14px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.filter-label {
  flex: 0 0 auto;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  width: 64px;
}

.seg {
  display: inline-flex;
  background: #f3f4f8;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}
.seg__btn {
  appearance: none;
  border: 0;
  background: transparent;
  color: #4b5563;
  font-size: 0.78rem;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.18s ease;
}
.seg__btn:hover { color: #0e5b46; }
.seg__btn--active {
  background: #ffffff;
  color: #0e5b46;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.filter-row--cats { border-top: 1px dashed #eef0f5; padding-top: 10px; }
.cat-scroll {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  flex: 1 1 auto;
  min-width: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.cat-scroll::-webkit-scrollbar { display: none; }
.cat-chip {
  flex: 0 0 auto;
  background: #fff;
  border: 1px solid #d8dde6;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 0.74rem;
  color: #4b5563;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
}
.cat-chip:hover { background: #f3f4fb; border-color: #c5cbd6; }
.cat-chip--active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff;
  border-color: transparent;
}

.src-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.src-pill--drugs { background: #e0f2fe; color: #075985; }
.src-pill--agro  { background: #dcfce7; color: #166534; }
.src-pill--vet   { background: #ede9fe; color: #5b21b6; }

.img-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1.5px dashed #c5cbd6;
  border-radius: 10px;
  background: #fbfcfe;
  padding: 18px;
  cursor: pointer;
  transition: all 0.18s ease;
  min-height: 84px;
}
.img-drop:hover { border-color: #16a07a; background: #f0fbf6; }
.img-drop--over { border-color: #16a07a; background: #e6f7ef; }
.img-drop--busy { cursor: progress; opacity: 0.85; }
.img-drop__hint {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.82rem;
  color: #4b5563;
}
.img-drop__hint strong { color: #1f2a44; font-weight: 600; }
.img-drop__hint span { font-size: 0.72rem; color: #6b7280; }

.img-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
  margin-top: 10px;
}
.img-thumb {
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e9f2;
  background: #f3f4f8;
}
.img-thumb img {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
}
.img-thumb__del {
  position: absolute;
  top: 4px; right: 4px;
  width: 22px; height: 22px;
  border: 0;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.78);
  color: #fff;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}
.img-thumb__del:hover { background: #b91c1c; }

.img-url-fallback {
  margin-top: 10px;
  font-size: 0.78rem;
}
.img-url-fallback summary {
  cursor: pointer;
  color: #6b7280;
  padding: 4px 0;
  user-select: none;
}
.img-url-fallback summary:hover { color: #0e5b46; }
.img-url-fallback textarea { margin-top: 6px; }
</style>
